import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  loadStatusColors,
  loadStatusTextMap,
} from '@rematter/modules/trade/utils/status';
import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedStatuses } from '../../../_state/brokeredLoadsList.state';
import { useTranslations } from '../../../i18n/useTranslations';

import { useTranslations as useTradeTranslations } from '~/modules/trade/i18n/useTranslations';

interface StatusOption {
  id: LoadStatus;
  label: string;
}

interface StatusColumnFilterProps {
  onClose?: () => void;
}

export const StatusColumnFilter: React.FC<StatusColumnFilterProps> = ({
  onClose,
}) => {
  const tBrokerage = useTranslations();
  const t = useTradeTranslations();
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const selectedStatuses = Array.from(selectedStatusesSet);

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  const statusOptions: StatusOption[] = React.useMemo(
    () =>
      Object.values(enumLoadStatus).map((status) => ({
        id: status,
        label: loadStatusTextMap[status](t, true),
      })),

    [],
  );

  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as LoadStatus[]));
    },
    [setSelectedStatusesSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.label}
        </div>
        <span
          className={cn('size-2 rounded-full', loadStatusColors[item.id])}
        />
      </div>
    ),
    [],
  );

  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  return (
    <MultiSelectFilter
      className="@StatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={
        tBrokerage.brokerage.loads.columnFilters.status.searchPlaceholder
      }
      isLoading={false}
      isFetching={false}
    />
  );
};
